import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Card,
  Alert,
  CircularProgress,
  ListSubheader,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Pagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SendIcon from "@mui/icons-material/Send";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import HistoryIcon from "@mui/icons-material/History";
import DeleteIcon from "@mui/icons-material/Delete";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import { PageHeader, ActionButton } from "../shared";

const MAX_MESSAGE_LENGTH = 500;

const MEMBER_ROLES = ["referee", "coach", "player", "parent"];
const formatRole = (role) => {
  if (!role || typeof role !== "string") return String(role ?? "");
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
};

const Alerts = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const profile = props?.props?.profile || props?.props?.profile?.data || {};
  const token = profile?.token || props?.props?.profile?.token;
  const userType =
    profile?.user_type || props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const senderName =
    profile?.organisationDetails?.name?.trim() ||
    profile?.organisation_name?.trim() ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
    null;
  // Admin-controlled flag (loaded into profile by AppLayout): may this club partner send alerts?
  const canSendAlerts =
    profile?.can_send_alerts ??
    props?.props?.profile?.can_send_alerts ??
    props?.props?.profile?.data?.can_send_alerts ??
    false;

  const [alertTab, setAlertTab] = useState(0); // 0 = to sub-orgs, 1 = to members, 2 = history
  const [subOrgs, setSubOrgs] = useState([]);
  const [loadingSubOrgs, setLoadingSubOrgs] = useState(true);
  const [message, setMessage] = useState("");
  const [subOrgIds, setSubOrgIds] = useState([]);
  const [sending, setSending] = useState(false);

  const [subOrgIdsMembers, setSubOrgIdsMembers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [sendingToMembers, setSendingToMembers] = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [alertPage, setAlertPage] = useState(1);

  const headers = useCallback(
    () => ({ "Content-Type": "application/json", "x-access-token": token }),
    [token]
  );

  const loadSubOrgs = async () => {
    if (!token) return;
    setLoadingSubOrgs(true);
    try {
      const data = await ApiServices.GetApiCall(ApiEndpoint.MASTER_ORG_SUB_ORGS, headers());
      setLoadingSubOrgs(false);
      if (data?.status && Array.isArray(data.data)) {
        setSubOrgs(data.data);
      } else {
        setSubOrgs([]);
      }
    } catch {
      setLoadingSubOrgs(false);
      setSubOrgs([]);
    }
  };

  const loadAlerts = useCallback(async () => {
    if (!token) return;
    setLoadingAlerts(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ALERTS_LIST,
        JSON.stringify({}),
        headers()
      );
      setLoadingAlerts(false);
      const rows = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.alerts)
        ? data.data.alerts
        : Array.isArray(data?.alerts)
        ? data.alerts
        : [];
      setAlerts(rows);
      setAlertPage(1);
    } catch {
      setLoadingAlerts(false);
      setAlerts([]);
    }
  }, [token, headers]);

  useEffect(() => {
    if (userType === "master_org" && token) {
      loadSubOrgs();
    }
  }, [userType, token]);

  useEffect(() => {
    if (userType === "master_org" && token && canSendAlerts) {
      loadAlerts();
    }
  }, [userType, token, canSendAlerts, loadAlerts]);

  const handleSend = async () => {
    const trimmed = (message || "").trim();
    if (!trimmed) { toast.error("Please enter a message."); return; }
    if (trimmed.length > MAX_MESSAGE_LENGTH) { toast.error(`Message must be at most ${MAX_MESSAGE_LENGTH} characters.`); return; }
    if (!token) return;

    setSending(true);
    const body = { message: trimmed };
    if (senderName) body.sender_name = senderName;
    if (subOrgIds.length > 0) body.sub_org_ids = subOrgIds.map((id) => Number(id));

    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ALERTS_SEND,
        JSON.stringify(body),
        headers()
      );
      setSending(false);
      if (data?.status) {
        toast.success(data.message || "Alert sent successfully.");
        setMessage("");
        setSubOrgIds([]);
        loadAlerts();
      } else {
        toast.error(data?.message || "Failed to send alert.");
      }
    } catch {
      setSending(false);
      toast.error("Failed to send alert.");
    }
  };

  const handleSendToMembers = async () => {
    const trimmed = (message || "").trim();
    if (!trimmed) { toast.error("Please enter a message."); return; }
    if (trimmed.length > MAX_MESSAGE_LENGTH) { toast.error(`Message must be at most ${MAX_MESSAGE_LENGTH} characters.`); return; }
    if (!subOrgIdsMembers.length) { toast.error("Please select at least one club."); return; }
    if (!token) return;

    setSendingToMembers(true);
    try {
      const subOrgIdsNum = subOrgIdsMembers.map((id) => Number(id));
      const seenIds = new Set();
      const allUserIds = [];

      for (const subOrgId of subOrgIdsNum) {
        const listData = await ApiServices.PostApiCall(
          ApiEndpoint.MASTER_ORG_USERS,
          JSON.stringify({ type: "user", pagination: false, sub_org_id: subOrgId, status: "active" }),
          headers()
        );
        if (listData?.status && Array.isArray(listData.data)) {
          const excluded = ["schools", "individual", "master_org", "sub_org", "master"];
          for (const u of listData.data) {
            if (excluded.includes(u.role)) continue;
            if (selectedRoles.length > 0 && !selectedRoles.includes(u.role)) continue;
            if (u.id && !seenIds.has(u.id)) {
              seenIds.add(u.id);
              allUserIds.push(Number(u.id));
            }
          }
        }
      }

      if (allUserIds.length === 0) {
        setSendingToMembers(false);
        toast.error(
          selectedRoles.length
            ? "No members with the selected role(s) found in the selected clubs."
            : "No members found in the selected clubs."
        );
        return;
      }

      const body = { message: trimmed, sub_org_ids: subOrgIdsNum, user_ids: allUserIds };
      if (senderName) body.sender_name = senderName;

      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ALERTS_SEND_TO_USERS,
        JSON.stringify(body),
        headers()
      );
      setSendingToMembers(false);
      if (data?.status) {
        toast.success(data.message || `Alert sent to ${allUserIds.length} member(s).`);
        setMessage("");
        setSubOrgIdsMembers([]);
        setSelectedRoles([]);
        loadAlerts();
      } else {
        toast.error(data?.message || "Failed to send alert.");
      }
    } catch {
      setSendingToMembers(false);
      toast.error("Failed to send alert.");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    setDeletingId(alertId);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ALERTS_DELETE,
        JSON.stringify({ alert_id: alertId }),
        headers()
      );
      setDeletingId(null);
      if (data?.status) {
        toast.success("Alert deleted.");
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      } else {
        toast.error(data?.message || "Failed to delete alert.");
      }
    } catch {
      setDeletingId(null);
      toast.error("Failed to delete alert.");
    }
    setConfirmDeleteId(null);
  };

  const allSubOrgIds = subOrgs.map((o) => o.id);
  const handleSelectAllSubOrgs = (e) => { e.stopPropagation(); setSubOrgIds([...allSubOrgIds]); };
  const handleClearSubOrgs = (e) => { e.stopPropagation(); setSubOrgIds([]); };
  const handleSelectAllSubOrgsMembers = (e) => { e.stopPropagation(); setSubOrgIdsMembers([...allSubOrgIds]); };
  const handleClearSubOrgsMembers = (e) => { e.stopPropagation(); setSubOrgIdsMembers([]); };
  const handleSelectAllRoles = (e) => { e.stopPropagation(); setSelectedRoles([...MEMBER_ROLES]); };
  const handleClearRoles = (e) => { e.stopPropagation(); setSelectedRoles([]); };

  const cardSx = {
    p: 0,
    overflow: "hidden",
    backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
    border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "#242424" : "#f7fafc",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
    },
  };
  const tabsSx = {
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 500,
      minHeight: 48,
      px: { xs: 1.5, sm: 2 },
      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
    },
    "& .MuiTabs-flexContainer": { gap: 0 },
  };

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader title="Alerts" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for club partner accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }

  if (!canSendAlerts) {
    return (
      <Box>
        <PageHeader
          title="Alerts"
          subtitle="Send short alerts to clubs or to all members of selected clubs."
          breadcrumbs={[{ label: "Club Partner" }, { label: "Alerts" }]}
        />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            Alerts are not enabled for your club partner account. Contact your administrator if you need this feature.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Alerts"
        subtitle="Send short alerts to clubs or to all members of selected clubs."
        breadcrumbs={[{ label: "Club Partner" }, { label: "Alerts" }]}
      />

      <Card sx={cardSx}>
        <Tabs
          value={alertTab}
          onChange={(_, v) => setAlertTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={tabsSx}
        >
          <Tab icon={<GroupIcon />} iconPosition="start" label="To clubs" />
          <Tab icon={<PersonIcon />} iconPosition="start" label="To members" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="History" />
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {alertTab === 0 && (
            <>
              <Alert
                severity="info"
                sx={{
                  mb: { xs: 2, md: 3 },
                  py: { xs: 1, md: 1.5 },
                  px: { xs: 1.5, md: 2 },
                  fontSize: { xs: "0.8125rem", md: "0.875rem" },
                  backgroundColor: isDark ? "rgba(99, 102, 241, 0.1)" : "rgba(25, 118, 210, 0.08)",
                  color: isDark ? "#cbd5e1" : "#1e3a5f",
                }}
              >
                Alerts are short messages (max {MAX_MESSAGE_LENGTH} characters) sent to club admins. Leave "Clubs" empty to send to all approved clubs.
              </Alert>

              <TextField
                label="Message"
                placeholder="e.g. Reminder: reports due Friday."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={4}
                minRows={3}
                fullWidth
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                helperText={`${(message || "").length} / ${MAX_MESSAGE_LENGTH}`}
                error={(message || "").length > MAX_MESSAGE_LENGTH}
                sx={{ mb: { xs: 2, md: 3 }, ...inputSx }}
              />

              <FormControl fullWidth sx={{ mb: { xs: 2, md: 3 }, ...inputSx }}>
                <InputLabel id="alerts-sub-orgs-label">Clubs (optional)</InputLabel>
                <Select
                  labelId="alerts-sub-orgs-label"
                  multiple
                  value={subOrgIds}
                  onChange={(e) => setSubOrgIds(e.target.value)}
                  input={<OutlinedInput label="Clubs (optional)" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>All approved clubs</Typography>;
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const org = subOrgs.find((o) => o.id === id);
                          return <Chip key={id} label={org?.name || id} size="small" sx={{ backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />;
                        })}
                      </Box>
                    );
                  }}
                  disabled={loadingSubOrgs}
                  MenuProps={{ autoFocus: false, slotProps: { root: { onMouseDown: (e) => e.stopPropagation() } } }}
                >
                  <ListSubheader sx={{ lineHeight: 1, py: 1, px: 1.5, display: "flex", gap: 1, flexWrap: "wrap", backgroundColor: "transparent" }}>
                    <Button size="small" onClick={handleSelectAllSubOrgs} onMouseDown={(e) => e.stopPropagation()} disabled={loadingSubOrgs || subOrgs.length === 0} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#818cf8" : "#1976d2", minWidth: "auto", px: 1 }}>Select all</Button>
                    <Button size="small" onClick={handleClearSubOrgs} onMouseDown={(e) => e.stopPropagation()} disabled={loadingSubOrgs} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#94a3b8" : "#64748b", minWidth: "auto", px: 1 }}>Clear</Button>
                  </ListSubheader>
                  {subOrgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.name || `Org #${org.id}`}</MenuItem>
                  ))}
                </Select>
                {loadingSubOrgs && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading clubs...</Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, display: "block" }}>
                  Leave empty to send to all approved clubs.
                </Typography>
              </FormControl>

              <ActionButton
                variant="primary"
                fullWidth
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={sending || !(message || "").trim()}
                sx={{ minHeight: 44 }}
              >
                {sending ? "Sending..." : "Send Alert"}
              </ActionButton>
            </>
          )}

          {alertTab === 1 && (
            <>
              <Alert
                severity="info"
                sx={{
                  mb: { xs: 2, md: 3 },
                  py: { xs: 1, md: 1.5 },
                  px: { xs: 1.5, md: 2 },
                  fontSize: { xs: "0.8125rem", md: "0.875rem" },
                  backgroundColor: isDark ? "rgba(99, 102, 241, 0.1)" : "rgba(25, 118, 210, 0.08)",
                  color: isDark ? "#cbd5e1" : "#1e3a5f",
                }}
              >
                Select clubs, then optionally filter by role. All matching members will receive the alert.
              </Alert>

              <TextField
                label="Message"
                placeholder="e.g. Reminder: reports due Friday."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={4}
                minRows={3}
                fullWidth
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                helperText={`${(message || "").length} / ${MAX_MESSAGE_LENGTH}`}
                error={(message || "").length > MAX_MESSAGE_LENGTH}
                sx={{ mb: { xs: 2, md: 3 }, ...inputSx }}
              />

              <FormControl fullWidth required sx={{ mb: { xs: 2, md: 3 }, ...inputSx }}>
                <InputLabel id="alerts-members-sub-orgs-label">Clubs (required)</InputLabel>
                <Select
                  labelId="alerts-members-sub-orgs-label"
                  multiple
                  value={subOrgIdsMembers}
                  onChange={(e) => setSubOrgIdsMembers(e.target.value)}
                  input={<OutlinedInput label="Clubs (required)" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>Select clubs whose members will receive the alert</Typography>;
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const org = subOrgs.find((o) => o.id === id);
                          return <Chip key={id} label={org?.name || id} size="small" sx={{ backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />;
                        })}
                      </Box>
                    );
                  }}
                  disabled={loadingSubOrgs}
                  MenuProps={{ autoFocus: false, slotProps: { root: { onMouseDown: (e) => e.stopPropagation() } } }}
                >
                  <ListSubheader sx={{ lineHeight: 1, py: 1, px: 1.5, display: "flex", gap: 1, flexWrap: "wrap", backgroundColor: "transparent" }}>
                    <Button size="small" onClick={handleSelectAllSubOrgsMembers} onMouseDown={(e) => e.stopPropagation()} disabled={loadingSubOrgs || subOrgs.length === 0} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#818cf8" : "#1976d2", minWidth: "auto", px: 1 }}>Select all</Button>
                    <Button size="small" onClick={handleClearSubOrgsMembers} onMouseDown={(e) => e.stopPropagation()} disabled={loadingSubOrgs} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#94a3b8" : "#64748b", minWidth: "auto", px: 1 }}>Clear</Button>
                  </ListSubheader>
                  {subOrgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.name || `Org #${org.id}`}</MenuItem>
                  ))}
                </Select>
                {loadingSubOrgs && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading clubs...</Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, display: "block" }}>
                  All members of the selected clubs will receive the alert.
                </Typography>
              </FormControl>

              <FormControl fullWidth sx={{ mb: { xs: 2, md: 3 }, ...inputSx }}>
                <InputLabel id="alerts-members-roles-label">Filter by role (optional)</InputLabel>
                <Select
                  labelId="alerts-members-roles-label"
                  multiple
                  value={selectedRoles}
                  onChange={(e) => setSelectedRoles(e.target.value)}
                  input={<OutlinedInput label="Filter by role (optional)" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>All roles</Typography>;
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((role) => (
                          <Chip key={role} label={formatRole(role)} size="small" sx={{ backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{ autoFocus: false, slotProps: { root: { onMouseDown: (e) => e.stopPropagation() } } }}
                >
                  <ListSubheader sx={{ lineHeight: 1, py: 1, px: 1.5, display: "flex", gap: 1, flexWrap: "wrap", backgroundColor: "transparent" }}>
                    <Button size="small" onClick={handleSelectAllRoles} onMouseDown={(e) => e.stopPropagation()} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#818cf8" : "#1976d2", minWidth: "auto", px: 1 }}>Select all</Button>
                    <Button size="small" onClick={handleClearRoles} onMouseDown={(e) => e.stopPropagation()} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#94a3b8" : "#64748b", minWidth: "auto", px: 1 }}>Clear</Button>
                  </ListSubheader>
                  {MEMBER_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>{formatRole(role)}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, display: "block" }}>
                  Leave empty to send to all roles.
                </Typography>
              </FormControl>

              <ActionButton
                variant="primary"
                fullWidth
                startIcon={sendingToMembers ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSendToMembers}
                disabled={sendingToMembers || !(message || "").trim() || subOrgIdsMembers.length === 0}
                sx={{ minHeight: 44 }}
              >
                {sendingToMembers ? "Sending..." : "Send to members"}
              </ActionButton>
            </>
          )}

          {alertTab === 2 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                  Sent Alerts
                </Typography>
                <Button
                  size="small"
                  onClick={loadAlerts}
                  disabled={loadingAlerts}
                  sx={{ textTransform: "none", color: isDark ? "#818cf8" : "#1976d2" }}
                >
                  {loadingAlerts ? <CircularProgress size={16} /> : "Refresh"}
                </Button>
              </Box>

              {loadingAlerts ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : alerts.length === 0 ? (
                <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", textAlign: "center", py: 6 }}>
                  No alerts sent yet.
                </Typography>
              ) : isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {alerts.slice((alertPage - 1) * 15, alertPage * 15).map((alert) => (
                    <Box
                      key={alert.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid #e2e8f0",
                        backgroundColor: isDark ? "#242424" : "#f8fafc",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1 }}>
                          {alert.message}
                        </Typography>
                        <Tooltip title="Delete alert">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDeleteId(alert.id)}
                            disabled={deletingId === alert.id}
                            sx={{ color: isDark ? "#f87171" : "#dc2626", flexShrink: 0, mt: -0.25 }}
                          >
                            {deletingId === alert.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {alert.sender_name && (
                        <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8", display: "block", mb: 0.75 }}>
                          From: {alert.sender_name}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {alert.sent_to?.type === "clubs" ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                              {(alert.sent_to.details || []).map((o) => (
                                <Chip key={o.id ?? o.name} label={o.name || "—"} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                              ))}
                            </Box>
                          ) : alert.sent_to?.type === "users" ? (
                            (() => {
                              const details = alert.sent_to.details || [];
                              const clubNames = [...new Set(details.map((r) => r.organisation_name).filter(Boolean))];
                              const roles = [...new Set(details.map((r) => r.role).filter(Boolean))];
                              return (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                  {clubNames.map((n) => (
                                    <Chip key={n} label={n} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                                  ))}
                                  {roles.length > 0
                                    ? roles.map((r) => (
                                        <Chip key={r} label={formatRole(r)} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(16,185,129,0.12)" : "rgba(5,150,105,0.08)", color: isDark ? "#34d399" : "#065f46" }} />
                                      ))
                                    : null
                                  }
                                </Box>
                              );
                            })()
                          ) : (
                            <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>—</Typography>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {alert.sent_at || alert.created_at
                            ? new Date(alert.sent_at || alert.created_at).toLocaleString()
                            : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600, fontSize: "0.8125rem", width: "45%" }}>Message</TableCell>
                        <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600, fontSize: "0.8125rem", width: "35%" }}>Sent to</TableCell>
                        <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600, fontSize: "0.75rem", width: "15%" }}>Date</TableCell>
                        <TableCell align="right" sx={{ width: "5%" }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.slice((alertPage - 1) * 15, alertPage * 15).map((alert) => (
                        <TableRow
                          key={alert.id}
                          sx={{
                            "&:last-child td": { border: 0 },
                            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" },
                          }}
                        >
                          <TableCell sx={{ color: isDark ? "#cbd5e1" : "#1e293b", maxWidth: 340 }}>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {alert.message}
                            </Typography>
                            {alert.sender_name && (
                              <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                                From: {alert.sender_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8125rem", maxWidth: 240 }}>
                            {alert.sent_to?.type === "clubs" ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {(alert.sent_to.details || []).map((o) => (
                                  <Chip key={o.id ?? o.name} label={o.name || "—"} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                                ))}
                              </Box>
                            ) : alert.sent_to?.type === "users" ? (
                              (() => {
                                const details = alert.sent_to.details || [];
                                const clubNames = [...new Set(details.map((r) => r.organisation_name).filter(Boolean))];
                                const roles = [...new Set(details.map((r) => r.role).filter(Boolean))];
                                return (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {clubNames.map((n) => (
                                      <Chip key={n} label={n} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                                    ))}
                                    {roles.length > 0
                                      ? roles.map((r) => (
                                          <Chip key={r} label={formatRole(r)} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(16,185,129,0.12)" : "rgba(5,150,105,0.08)", color: isDark ? "#34d399" : "#065f46" }} />
                                        ))
                                      : null
                                    }
                                  </Box>
                                );
                              })()
                            ) : (
                              <Typography variant="body2" sx={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "0.8125rem" }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                            {alert.sent_at || alert.created_at
                              ? new Date(alert.sent_at || alert.created_at).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete alert">
                              <IconButton
                                size="small"
                                onClick={() => setConfirmDeleteId(alert.id)}
                                disabled={deletingId === alert.id}
                                sx={{ color: isDark ? "#f87171" : "#dc2626" }}
                              >
                                {deletingId === alert.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!loadingAlerts && alerts.length > 15 && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                  <Pagination
                    count={Math.ceil(alerts.length / 15)}
                    page={alertPage}
                    onChange={(_, v) => setAlertPage(v)}
                    size="small"
                    sx={{ "& .MuiPaginationItem-root": { color: isDark ? "#94a3b8" : "#64748b" } }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Card>

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => { if (!deletingId) setConfirmDeleteId(null); }}
        disableEscapeKeyDown={!!deletingId}
        PaperProps={{
          sx: { backgroundColor: isDark ? "#1e1e1e" : "#fff", backgroundImage: "none", minWidth: 320 },
        }}
      >
        <DialogTitle sx={{ color: isDark ? "#e2e8f0" : "#1e293b" }}>Delete alert?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
            This will permanently delete the alert and all associated data. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} sx={{ textTransform: "none", color: isDark ? "#94a3b8" : "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteAlert(confirmDeleteId)}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            disabled={deletingId === confirmDeleteId}
          >
            {deletingId === confirmDeleteId ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Alerts;
